import { Skeleton } from "@/shared/components/ui/skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  PersistenceStateUsingContext,
  usePersistenceStateUpdateOnTrigger,
} from "@/shared/lib/persistence-state/persistence-state-info";
import {
  useQpmDocumentationAddMutation,
  useQpmDocumentationDeleteMutation,
  useQpmDocumentationListQuery,
} from "@/services/backend/qpm/documentation/api/documentation-api";
import {
  Documentation,
  DocumentationList,
  PutRequest,
} from "@/services/backend/qpm/documentation/types/documentation";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Input } from "@/shared/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { formatDate } from "@/shared/lib/utilities/date";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";

export function DocumentationsRoutePage() {
  const { data: list, isLoading, error } = useQpmDocumentationListQuery({});

  if (isLoading) {
    return <Skeleton className="h-36 w-full" />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-end justify-between">
          <CardTitle>{t("QPM Dokumentationen")}</CardTitle>
          <PersistenceStateUsingContext />
        </div>
        <CardDescription>
          <p>
            {t(
              "Sie können hier die Dokumentationen die unter anderem in der Kalkulation benötigt werden bearebiten.",
            )}
          </p>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <Skeleton className="h-36 w-full" />}
        {!isLoading && list && <DocumentationsCardContent list={list} />}
      </CardContent>
    </Card>
  );
}

function DocumentationsCardContent({ list }: { list: DocumentationList }) {
  const [put, { error, isLoading, isSuccess, reset }] =
    useQpmDocumentationAddMutation();

  const docs: Documentation[] = useMemo(() => list.docs ?? [], [list.docs]);

  const sortedDocs: Documentation[] = useMemo(
    () => [...docs].sort((a, b) => a.name.localeCompare(b.name)),
    [docs],
  );

  const existingKeys = useMemo(
    () => new Set(docs.map((d) => String(d.key).toLowerCase())),
    [docs],
  );

  const [newRowId, setNewRowId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // DELETE
  const [deleteRequest, setDeleteRequest] = useState<{ id: string }>({
    id: "",
  });

  const [
    del,
    {
      error: deleteError,
      isLoading: deleteIsLoading,
      isSuccess: deleteIsSuccess,
      reset: deleteReset,
    },
  ] = useQpmDocumentationDeleteMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    deleteRequest,
    del,
    deleteIsLoading,
    deleteError,
    deleteIsSuccess,
    deleteReset,
    100,
  );

  const [request, setRequest] = useState<PutRequest>({});

  const isCreatingNew = editingId != null && editingId === newRowId;

  // Keep request in sync when switching edit row.
  // - If we are editing an existing entry, copy it into request.
  // - If we are creating a new entry (editingId === newRowId), initialize request once.
  useEffect(() => {
    if (!editingId) return;

    if (newRowId && editingId === newRowId) {
      // New row: initialize only if request not already set for this id.
      if (request.id !== newRowId) {
        setRequest({
          id: newRowId,
          name: "",
          key: "",
          duration: 0,
          createdAt: "",
        });
      }
      return;
    }

    const row = sortedDocs.find((d) => d.id === editingId);
    if (!row) return;

    setRequest({
      id: row.id,
      name: row.name,
      key: row.key,
      duration: row.duration,
      createdAt: row.createdAt,
    });
  }, [editingId, newRowId, request.id, sortedDocs]);

  const { onTrigger: triggerSave } = usePersistenceStateUpdateOnTrigger(
    request,
    put,
    isLoading,
    error,
    isSuccess,
    reset,
    { toastError: true, toastSuccess: true },
  );

  // After save succeeded, leave edit mode and clear "new row" guard.
  useEffect(() => {
    if (!isSuccess) return;

    setEditingId(null);
    if (isCreatingNew) {
      setNewRowId(null);
    }

    reset(); // reset mutation state, otherwise, the isSuccess flag would stay true
  }, [isSuccess, isCreatingNew, reset]);

  const canAddRow = newRowId == null && editingId == null;

  const startEdit = (doc: Documentation) => {
    setEditingId(doc.id);
  };

  const cancelEdit = () => {
    setEditingId(null);

    if (newRowId) {
      setNewRowId(null);
    }

    setRequest({});
  };

  const validateRequest = (req: PutRequest): string | null => {
    if (!req.id) return t("ID fehlt");
    if (!req.name || req.name.trim().length === 0) return t("Name fehlt");
    if (!req.key || String(req.key).trim().length === 0)
      return t("Bezeichner fehlt");
    if (req.duration == null) return t("Dauer fehlt");

    const strictKey = toNormalizedDocKey(String(req.key));
    if (strictKey.length === 0) return t("Bezeichner fehlt");

    if (isCreatingNew) {
      if (existingKeys.has(strictKey)) {
        return t("Bezeichner bereits vorhanden");
      }
    }

    return null;
  };

  const onSave = () => {
    const validationError = validateRequest(request);
    if (validationError) {
      return;
    }

    setRequest({
      ...request,
      key: toNormalizedDocKey(String(request.key ?? "")),
    });

    triggerSave();
  };

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30%]">{t("Name")}</TableHead>
            <TableHead className="w-[20%]">{t("Bezeichner")}</TableHead>
            <TableHead className="w-[20%]">{t("Dauer")}</TableHead>
            <TableHead className="w-[20%]">{t("Erstellt am")}</TableHead>
            <TableHead className="w-[10%] text-right">
              {t("Bearbeiten")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDocs.map((doc) => (
            <DocumentationRow
              key={doc.id}
              doc={doc}
              isEditMode={editingId === doc.id}
              isNew={false}
              request={request}
              setRequest={setRequest}
              onEdit={() => startEdit(doc)}
              onCancel={cancelEdit}
              onSave={onSave}
              // Delete is intentionally only exposed while editing.
              onDelete={() => {
                setDeleteRequest({ id: doc.id });
              }}
              existingKeys={existingKeys}
            />
          ))}

          {newRowId && editingId === newRowId && (
            <DocumentationRow
              key={newRowId}
              doc={{
                id: newRowId,
                name: "",
                key: "",
                duration: 0,
                createdAt: "",
              }}
              isEditMode
              isNew
              request={request}
              setRequest={setRequest}
              onEdit={() => setEditingId(newRowId)}
              onCancel={cancelEdit}
              onSave={onSave}
              onDelete={undefined}
              existingKeys={existingKeys}
            />
          )}

          {sortedDocs.length === 0 && !newRowId && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-6">
                {t("Keine Dokumentationen gefunden.")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-start">
        <Button
          disabled={!canAddRow}
          onClick={() => {
            const id = v4();
            setNewRowId(id);
            setEditingId(id);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("hinzufügen")}
        </Button>
      </div>
    </div>
  );
}

function toDocKeyInputValue(input: string): string {
  // Typing-time sanitizer:
  // - lowercases
  // - converts whitespace to '-'
  // - keeps '-' if user types it
  // - removes invalid chars
  // - collapses multiple dashes
  // - does NOT trim leading/trailing dashes (so typing '-' or ' ' still shows something)
  let s = input.toLowerCase();

  // German transliteration
  s = s
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");

  s = s.replace(/\s+/g, "-");
  s = s.replace(/_+/g, "-");
  s = s.replace(/[^a-z0-9-]/g, "");
  s = s.replace(/-+/g, "-");
  return s;
}

function toNormalizedDocKey(input: string): string {
  // Strict kebab-case normalizer for saving
  let s = toDocKeyInputValue(input);
  s = s.replace(/^-/g, "").replace(/-$/g, "");
  return s;
}

function DocumentationRow({
  doc,
  isEditMode,
  isNew,
  request,
  setRequest,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  existingKeys,
}: {
  doc: Documentation;
  isEditMode: boolean;
  isNew: boolean;
  request: PutRequest;
  setRequest: (req: PutRequest) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
  existingKeys: Set<string>;
}) {
  const [keyInput, setKeyInput] = useState<string>("");
  const [keyTouched, setKeyTouched] = useState<boolean>(false);

  useEffect(() => {
    if (!isEditMode) return;
    setKeyInput(String((isNew ? request.key : doc.key) ?? ""));
    setKeyTouched(false);
  }, [isEditMode, isNew, request.key, doc.key]);

  const setName = (name: string) => {
    setRequest({ ...request, name });

    // Auto-derive key only for new rows and only until user touched the key manually.
    if (!isNew || keyTouched) return;

    const derived = toDocKeyInputValue(name);
    if (!derived) return;

    const derivedStrict = toNormalizedDocKey(derived);
    if (derivedStrict.length === 0) return;

    // Prevent auto-setting duplicates (user can still adjust manually)
    if (existingKeys.has(derivedStrict)) return;

    setKeyInput(derived);
    setRequest({
      ...request,
      name,
      key: derived,
    });
  };

  if (isEditMode) {
    return (
      <TableRow>
        <TableCell className="py-1.5">
          <Input
            type="text"
            placeholder={t("Name")}
            value={request.name ?? ""}
            onChange={(e) => setName(e.target.value)}
          />
        </TableCell>
        <TableCell className="py-1.5">
          <Input
            type="text"
            placeholder={t("Bezeichner")}
            value={isNew ? keyInput : String(doc.key)}
            disabled={!isNew}
            onChange={(e) => {
              if (!isNew) return;

              setKeyTouched(true);

              const raw = e.target.value;
              const typed = toDocKeyInputValue(raw);
              setKeyInput(typed);

              const maybeDuplicate =
                typed.length > 0 && existingKeys.has(toNormalizedDocKey(typed));

              setRequest({
                ...request,
                key: maybeDuplicate ? request.key : typed,
              });
            }}
          />
        </TableCell>
        <TableCell className="py-1.5">
          <TimeNumberInput
            value={request.duration ?? 0}
            onChange={(val) => setRequest({ ...request, duration: val })}
          />
        </TableCell>
        <TableCell className="py-1.5">
          <Input type="text" value={formatDate(doc.createdAt)} disabled />
        </TableCell>
        <TableCell className="py-1.5 text-right">
          <div className="flex items-center justify-end">
            <TooltipProvider>
              {/* Delete (only for existing rows) */}
              {!isNew && onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="mr-1"
                      size="sm"
                      variant="destructive"
                      onClick={onDelete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("Löschen")}</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Cancel */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="mr-1"
                    size="sm"
                    variant="outline"
                    onClick={onCancel}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Abbrechen")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Save */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="default" onClick={onSave}>
                    <Save className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Speichern")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="py-1.5 font-medium">{doc.name}</TableCell>
      <TableCell className="py-1.5">{doc.key}</TableCell>
      <TableCell className="py-1.5">
        <TimeNumberInput value={doc.duration} disabled />
      </TableCell>
      <TableCell className="py-1.5">{formatDate(doc.createdAt)}</TableCell>
      <TableCell className="py-1.5 text-right">
        <div className="flex items-center justify-end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("Bearbeiten")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );
}
